import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ShoppingBag, ShoppingCart, X, Plus, Minus, Sparkles, Wallet, Tag, Check,
  Briefcase, Hotel as HotelIcon, Heart, Utensils, Compass,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_ICONS = { Briefcase, Hotel: HotelIcon, Sparkles, Heart, Utensils, Compass };
const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export const LiinkShoppingPage = () => {
  // STAGE 2 — Shop temporarily paused, replaced with Coming Soon landing.
  return <ComingSoonShop />;
};

const ComingSoonShop = () => (
  <div className="min-h-[78vh] bg-gradient-to-b from-[#FFF5F8] via-white to-white flex items-center" data-testid="shop-coming-soon">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FFE5EE] text-[#FF0059] text-[11px] font-black uppercase tracking-wider mb-5">
        <Sparkles className="h-3.5 w-3.5" /> COMING SOON
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-black leading-[1.05] mb-5" data-testid="shop-coming-title">
        LiinkShopping is on its way.
      </h1>
      <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto mb-8">
        We're upgrading our wallet-only shopping experience. Stock up on travel essentials,
        amenities and gifting — soon redeemable entirely with your localliink rewards.
      </p>
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-white border border-gray-200 shadow-sm">
        <Wallet className="h-5 w-5 text-[#FF0059]" />
        <p className="text-sm font-bold text-black">
          Keep earning cashback — your wallet will redeem here when we relaunch.
        </p>
      </div>
    </div>
  </div>
);

// eslint-disable-next-line no-unused-vars
const LiinkShoppingPage_legacy = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [cart, setCart] = useState({}); // { sku: { product, quantity } }
  const [showCart, setShowCart] = useState(false);
  const [checkout, setCheckout] = useState({ guest_code: '', delivery_name: '', delivery_phone: '', delivery_address: '' });
  const [walletInfo, setWalletInfo] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/shop/products`),
        axios.get(`${API}/shop/categories`),
      ]);
      setProducts(pRes.data);
      setCategories(cRes.data);
    } catch (e) {
      toast.error('Failed to load store');
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Prefill from logged-in user's profile
  useEffect(() => {
    if (user?.role === 'guest') {
      axios.get(`${API}/guest/profile`, { withCredentials: true })
        .then(({ data }) => {
          setCheckout((p) => ({ ...p, guest_code: data.guest_code, delivery_name: data.name || '', delivery_phone: data.phone || '' }));
        })
        .catch(() => {});
    }
  }, [user]);

  const visibleProducts = activeCat === 'all' ? products : products.filter((p) => p.category === activeCat);

  const addToCart = (product) => {
    setCart((c) => {
      const existing = c[product.sku] || { product, quantity: 0 };
      return { ...c, [product.sku]: { product, quantity: existing.quantity + 1 } };
    });
    toast.success(`${product.name} added to cart`);
  };

  const updateQty = (sku, delta) => {
    setCart((c) => {
      const item = c[sku];
      if (!item) return c;
      const q = item.quantity + delta;
      if (q <= 0) {
        const { [sku]: _, ...rest } = c;
        return rest;
      }
      return { ...c, [sku]: { ...item, quantity: q } };
    });
  };

  const cartItems = Object.values(cart);
  const cartTotal = cartItems.reduce((s, i) => s + i.product.price * i.quantity, 0);
  const cartCount = cartItems.reduce((s, i) => s + i.quantity, 0);

  const validateCode = async () => {
    if (!checkout.guest_code) return;
    try {
      const { data } = await axios.post(`${API}/guest/wallet/validate-code`, { guest_code: checkout.guest_code });
      setWalletInfo(data);
    } catch (e) {
      setWalletInfo(null);
      toast.error(e.response?.data?.detail || 'Invalid guest code');
    }
  };

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please sign in to checkout');
      navigate('/login');
      return;
    }
    if (cartItems.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    setPlacing(true);
    try {
      const { data } = await axios.post(`${API}/shop/checkout`, {
        items: cartItems.map((i) => ({ sku: i.product.sku, quantity: i.quantity })),
        guest_code: checkout.guest_code,
        delivery_name: checkout.delivery_name,
        delivery_phone: checkout.delivery_phone,
        delivery_address: checkout.delivery_address,
      }, { withCredentials: true });
      setOrderSuccess(data.order);
      setCart({});
      setShowCart(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Order failed');
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="liink-shopping-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-[#FFE5EE] via-white to-white py-12 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FF0059] text-white text-xs font-bold mb-3">
                <Sparkles className="h-3.5 w-3.5" /> NEW · LIVE NOW
              </div>
              <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-black">
                Local<span className="text-[#FF0059]">Shop</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-600 mt-2 max-w-2xl">
                {t('shop.subtitle')}
              </p>
            </div>
            <Button
              onClick={() => setShowCart(true)}
              className="bg-black hover:bg-gray-800 text-white rounded-full px-5 py-3 font-bold relative"
              data-testid="open-cart-button"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              {t('shop.cart')}
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#FF0059] text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-black" data-testid="cart-badge">
                  {cartCount}
                </span>
              )}
            </Button>
          </div>
        </div>
      </section>

      {/* Category Pills */}
      <section className="border-b border-gray-100 py-4 sticky top-16 md:top-20 bg-white z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-2 overflow-x-auto">
          <button
            onClick={() => setActiveCat('all')}
            className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${activeCat === 'all' ? 'bg-[#FF0059] text-white' : 'bg-gray-100 text-black hover:bg-gray-200'}`}
            data-testid="cat-all"
          >
            All ({products.length})
          </button>
          {categories.map((c) => {
            const Icon = CAT_ICONS[c.icon] || ShoppingBag;
            const count = products.filter((p) => p.category === c.id).length;
            return (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${activeCat === c.id ? 'bg-[#FF0059] text-white' : 'bg-gray-100 text-black hover:bg-gray-200'}`}
                data-testid={`cat-${c.id}`}
              >
                <Icon className="h-3.5 w-3.5" /> {c.label} ({count})
              </button>
            );
          })}
        </div>
      </section>

      {/* Products grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {visibleProducts.map((p) => {
            const inCartQty = cart[p.sku]?.quantity || 0;
            return (
              <div key={p.sku} className="group" data-testid={`product-${p.sku}`}>
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 mb-3 relative">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {p.stock < 10 && p.stock > 0 && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-orange-500 text-white text-[10px] font-black uppercase tracking-wide">
                      Only {p.stock} left
                    </div>
                  )}
                  {p.stock === 0 && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <span className="text-white font-black text-sm">SOLD OUT</span>
                    </div>
                  )}
                </div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#FF0059]">{p.category_label}</p>
                <h3 className="font-bold text-base leading-tight text-black mt-1 line-clamp-2">{p.name}</h3>
                <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{p.tagline}</p>
                <div className="flex items-center justify-between mt-3">
                  <p className="text-lg font-black text-black">{formatINR(p.price)}</p>
                  {inCartQty > 0 ? (
                    <div className="inline-flex items-center gap-1 bg-[#FFE5EE] rounded-full p-1">
                      <button onClick={() => updateQty(p.sku, -1)} className="w-7 h-7 rounded-full bg-white text-[#FF0059] flex items-center justify-center hover:bg-gray-100" data-testid={`qty-minus-${p.sku}`}>
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="font-bold text-sm w-6 text-center" data-testid={`qty-${p.sku}`}>{inCartQty}</span>
                      <button onClick={() => updateQty(p.sku, 1)} disabled={inCartQty >= p.stock} className="w-7 h-7 rounded-full bg-[#FF0059] text-white flex items-center justify-center hover:bg-[#CC0047] disabled:opacity-50" data-testid={`qty-plus-${p.sku}`}>
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => addToCart(p)}
                      disabled={p.stock === 0}
                      className="bg-[#FF0059] hover:bg-[#CC0047] rounded-full text-xs font-bold disabled:opacity-50"
                      data-testid={`add-to-cart-${p.sku}`}
                    >
                      Add
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Cart Drawer */}
      {showCart && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setShowCart(false)} data-testid="cart-drawer">
          <div className="bg-white w-full max-w-md h-full overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black flex items-center gap-2">
                <ShoppingCart className="h-6 w-6 text-[#FF0059]" /> Cart ({cartCount})
              </h2>
              <button onClick={() => setShowCart(false)} data-testid="close-cart">
                <X className="h-5 w-5" />
              </button>
            </div>

            {cartItems.length === 0 ? (
              <p className="text-center py-10 text-gray-500" data-testid="empty-cart">Your cart is empty</p>
            ) : (
              <>
                <div className="space-y-3 mb-6">
                  {cartItems.map((i) => (
                    <div key={i.product.sku} className="flex gap-3" data-testid={`cart-item-${i.product.sku}`}>
                      <img src={i.product.image} alt={i.product.name} className="w-16 h-16 rounded-lg object-cover bg-gray-100" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm line-clamp-1">{i.product.name}</p>
                        <p className="text-xs text-gray-500">{formatINR(i.product.price)} × {i.quantity}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <button onClick={() => updateQty(i.product.sku, -1)} className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                          <span className="text-xs font-bold w-6 text-center">{i.quantity}</span>
                          <button onClick={() => updateQty(i.product.sku, 1)} className="w-6 h-6 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                        </div>
                      </div>
                      <p className="font-black text-sm">{formatINR(i.product.price * i.quantity)}</p>
                    </div>
                  ))}
                </div>

                <div className="bg-[#FAFAFA] rounded-xl p-4 mb-4">
                  <div className="flex justify-between font-black text-lg">
                    <span>Total</span>
                    <span>{formatINR(cartTotal)}</span>
                  </div>
                </div>

                <form onSubmit={placeOrder} className="space-y-3" data-testid="checkout-form">
                  <div className="p-3 border border-gray-200 rounded-xl bg-[#FFE5EE]/30">
                    <Label className="text-xs font-bold text-[#CC0047] flex items-center gap-1 mb-1">
                      <Tag className="h-3 w-3" /> Guest Code (pay 100% with wallet)
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="AA12345SH"
                        value={checkout.guest_code}
                        onChange={(e) => setCheckout({ ...checkout, guest_code: e.target.value.toUpperCase() })}
                        maxLength={9}
                        required
                        className="uppercase tracking-widest font-mono font-bold"
                        data-testid="checkout-guest-code"
                      />
                      <Button type="button" onClick={validateCode} variant="outline" className="border-[#FF0059] text-[#FF0059]" data-testid="validate-wallet-button">
                        <Wallet className="h-4 w-4" />
                      </Button>
                    </div>
                    {walletInfo && (
                      <p className="text-xs mt-2 font-bold text-[#CC0047]" data-testid="wallet-balance-display">
                        {walletInfo.owner_name} · Available: ₹{walletInfo.balance.toLocaleString('en-IN')}
                      </p>
                    )}
                    {walletInfo && walletInfo.balance < cartTotal && (
                      <p className="text-xs mt-1 text-red-600 font-bold">Insufficient balance for this cart</p>
                    )}
                  </div>
                  <Input placeholder="Delivery name" required value={checkout.delivery_name} onChange={(e) => setCheckout({ ...checkout, delivery_name: e.target.value })} data-testid="checkout-name" />
                  <Input placeholder="Phone" required value={checkout.delivery_phone} onChange={(e) => setCheckout({ ...checkout, delivery_phone: e.target.value })} data-testid="checkout-phone" />
                  <textarea
                    placeholder="Delivery address"
                    required
                    className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                    rows="3"
                    value={checkout.delivery_address}
                    onChange={(e) => setCheckout({ ...checkout, delivery_address: e.target.value })}
                    data-testid="checkout-address"
                  />
                  <Button
                    type="submit"
                    disabled={placing || !walletInfo || walletInfo.balance < cartTotal}
                    className="w-full bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold py-6 rounded-xl text-base disabled:opacity-50"
                    data-testid="place-order-button"
                  >
                    {placing ? 'Placing order...' : `Pay ${formatINR(cartTotal)} from wallet`}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Order success modal */}
      {orderSuccess && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="order-success-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF0059] text-white flex items-center justify-center">
              <Check className="h-8 w-8" />
            </div>
            <h2 className="text-2xl font-black mb-2">Order Confirmed!</h2>
            <p className="text-gray-600 mb-4">Order #{orderSuccess._id.slice(0, 8)} · {formatINR(orderSuccess.total)} debited from wallet</p>
            <p className="text-xs text-gray-500 mb-6">You'll receive a confirmation email shortly. Delivery in 3-5 business days.</p>
            <Button onClick={() => setOrderSuccess(null)} className="bg-[#FF0059] hover:bg-[#CC0047] w-full" data-testid="order-success-close">
              Continue Shopping
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
